import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useNotificationData } from './hooks/useNotificationData';
import { useNotificationHandlers } from './hooks/useNotificationHandlers';
import { notificationApi } from './services/notificationApi';
import { useSocketService } from './services/socketService';
import type { NotificationContextType } from './types/notification.types';

// Import with alias to avoid conflict with global Notification
import type { Notification as AppNotification } from './types/notification.types';
import {
  requestBrowserNotificationPermission,
  showBrowserNotification,
} from './utils/notificationUtils';

// ==================== Context ====================
const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined,
);

// ==================== Provider ====================
export const NotificationProvider = React.memo(function NotificationProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const socketService = useSocketService();
  const [notificationPermission, setNotificationPermission] =
    useState<NotificationPermission | null>(null);
  const [connectionAttempts, setConnectionAttempts] = useState(0);
  const [wasPageHidden, setWasPageHidden] = useState(false);
  const [lastActiveTime, setLastActiveTime] = useState<number>(Date.now());

  const BASE_URL = import.meta.env.VITE_HEALTHY_EMPIRE_SERVER;
  const userId = localStorage.getItem('user_id');

  // Use custom hooks
  const {
    notifications,
    setNotifications,
    unreadCount,
    setUnreadCount,
    loading,
    currentPage,
    hasMore,
    totalPages,
    totalNotifications,
    setTotalNotifications,
    loadMoreNotifications,
    getUnreadNotifications,
    refreshNotifications,
    fetchNotifications,
    fetchUnreadCount,
  } = useNotificationData(userId);

  const {
    handleNewNotification,
    handleConnect,
    handleDisconnect,
    handleError,
    markAsRead,
    markAllAsRead,
  } = useNotificationHandlers({
    notificationPermission,
    setNotifications,
    setUnreadCount,
    setTotalNotifications,
    setConnectionAttempts,
  });

  // Listen for custom notification events (for testing purposes)
  useEffect(() => {
    const handleCustomNotification = (event: Event) => {
      const customEvent = event as CustomEvent<unknown>;
      if (customEvent.detail && typeof customEvent.detail === 'object') {
        // Type guard to check if the object has the required properties
        const detail = customEvent.detail as Record<string, unknown>;
        if (
          typeof detail._id === 'string' &&
          typeof detail.title === 'string' &&
          typeof detail.type === 'string' &&
          typeof detail.is_read === 'boolean' &&
          typeof detail.createdAt === 'string'
        ) {
          // Safe to cast to AppNotification
          handleNewNotification(detail as unknown as AppNotification);
        }
      }
    };

    document.addEventListener('custom-notification', handleCustomNotification);

    return () => {
      document.removeEventListener(
        'custom-notification',
        handleCustomNotification,
      );
    };
  }, [handleNewNotification]);

  // Request browser notification permission
  const requestNotificationPermission =
    useCallback(async (): Promise<NotificationPermission> => {
      const permission = await requestBrowserNotificationPermission();
      setNotificationPermission(permission);
      return permission;
    }, []);

  // Check for missed notifications when page becomes visible
  const checkForMissedNotifications = useCallback(async () => {
    if (!userId) return;

    try {
      // Use the existing notification API service to fetch notifications after last active time
      // This ensures consistency with authentication and API structure
      const data = await notificationApi.fetchNotificationsAfter(
        lastActiveTime,
        10,
      );
      const missedNotifications = data.items || [];

      // Show browser notifications for missed notifications if permission granted
      if (
        notificationPermission === 'granted' &&
        missedNotifications.length > 0
      ) {
        for (const notification of missedNotifications) {
          if (!notification.is_read) {
            await showBrowserNotification(notification, markAsRead);
            // Add small delay between notifications to avoid overwhelming user
            await new Promise((resolve) => setTimeout(resolve, 1000));
          }
        }
      }

      // Update notifications state
      if (missedNotifications.length > 0) {
        setNotifications((prev) => {
          const existingIds = new Set(prev.map((n: any) => n._id));
          const newNotifications = missedNotifications.filter(
            (n: any) => !existingIds.has(n._id),
          );
          return [...newNotifications, ...prev];
        });

        // Update unread count
        const newUnreadCount = missedNotifications.filter(
          (n: any) => !n.is_read,
        ).length;
        if (newUnreadCount > 0) {
          setUnreadCount((prev) => prev + newUnreadCount);
          setTotalNotifications((prev) => prev + missedNotifications.length);
        }
      }
    } catch (error) {
      console.error('Failed to check for missed notifications:', error);
    }
  }, [
    userId,
    lastActiveTime,
    notificationPermission,
    markAsRead,
    setNotifications,
    setUnreadCount,
    setTotalNotifications,
  ]);

  // Initialize socket
  useEffect(() => {
    if (!userId) return;

    socketService.connect(userId, BASE_URL || '', {
      onNotification: handleNewNotification,
      onConnect: handleConnect,
      onDisconnect: handleDisconnect,
      onError: handleError,
    });

    return () => {
      socketService.disconnect();
    };
  }, [
    userId,
    BASE_URL,
    socketService,
    handleNewNotification,
    handleConnect,
    handleDisconnect,
    handleError,
  ]);

  // Add visibility change listener with enhanced functionality
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        setWasPageHidden(true);
        setLastActiveTime(Date.now());
      } else if (document.visibilityState === 'visible' && wasPageHidden) {
        // Page became visible after being hidden
        setWasPageHidden(false);

        // Check for missed notifications after a short delay
        setTimeout(() => {
          checkForMissedNotifications();
        }, 1000);
      }
    };

    // Handle page load - check for notifications if browser was closed
    const handlePageLoad = () => {
      const lastVisit = localStorage.getItem('lastVisitTime');
      const currentTime = Date.now();

      if (lastVisit) {
        const timeDiff = currentTime - parseInt(lastVisit);
        // If more than 5 minutes since last visit, check for missed notifications
        if (timeDiff > 5 * 60 * 1000) {
          setLastActiveTime(parseInt(lastVisit));
          setTimeout(() => {
            checkForMissedNotifications();
          }, 2000); // Delay to allow other initialization
        }
      }

      localStorage.setItem('lastVisitTime', currentTime.toString());
    };

    // Handle page unload - store last visit time
    const handlePageUnload = () => {
      localStorage.setItem('lastVisitTime', Date.now().toString());
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('load', handlePageLoad);
    window.addEventListener('beforeunload', handlePageUnload);

    // Check on initial load
    handlePageLoad();

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('load', handlePageLoad);
      window.removeEventListener('beforeunload', handlePageUnload);
    };
  }, [
    wasPageHidden,
    lastActiveTime,
    userId,
    notificationPermission,
    checkForMissedNotifications,
  ]);

  // Polling fallback if WebSocket fails
  useEffect(() => {
    if (connectionAttempts < 3) return;

    const poll = setInterval(() => {
      fetchNotifications(1, true);
      fetchUnreadCount();
    }, 30000);

    return () => clearInterval(poll);
  }, [connectionAttempts, fetchNotifications, fetchUnreadCount]);

  // Initialize notification permission and request if not set
  useEffect(() => {
    if ('Notification' in window) {
      const currentPermission = Notification.permission;
      setNotificationPermission(currentPermission);

      // Auto-request permission if not determined
      if (currentPermission === 'default') {
        // Request permission after a short delay to not interrupt initial page load
        setTimeout(() => {
          requestNotificationPermission();
        }, 3000);
      }
    }
  }, [requestNotificationPermission]);

  // Memoize context value with stable references
  const contextValue = useMemo(
    () => ({
      notifications,
      unreadCount,
      markAsRead,
      markAllAsRead,
      requestNotificationPermission,
      notificationPermission,
      loading,
      hasMore,
      currentPage,
      totalPages,
      totalNotifications,
      loadMoreNotifications,
      getUnreadNotifications,
      refreshNotifications,
    }),
    [
      notifications,
      unreadCount,
      markAsRead,
      markAllAsRead,
      requestNotificationPermission,
      notificationPermission,
      loading,
      hasMore,
      currentPage,
      totalPages,
      totalNotifications,
      loadMoreNotifications,
      getUnreadNotifications,
      refreshNotifications,
    ],
  );

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
});

// ==================== Custom Hook ====================
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      'useNotifications must be used within NotificationProvider',
    );
  }
  return context;
};
